/**
 * OmegaBriefingPage — /omega/briefing
 *
 * Phase 19 founder daily briefing. Magazine-style readable layout.
 * Sections: overnight summary, decisions, threats, revenue, memory, recommendation.
 * Bottom: archive list (last 14 days). Mobile-responsive.
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import {
  AlertTriangle,
  BookOpen,
  Brain,
  ChevronRight,
  Clock,
  DollarSign,
  Shield,
  Sparkles,
  TrendingUp,
  Zap,
} from 'lucide-react';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { api } from '@/api/client';
import { cn } from '@/utils/cn';
import type { FounderBriefing } from '@/api/types';

// ─── helpers ────────────────────────────────────────────────────────

function formatDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      weekday: 'long', year: 'numeric', month: 'long', day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function formatShortDate(iso: string): string {
  try {
    return new Intl.DateTimeFormat('en-US', {
      month: 'short', day: 'numeric',
    }).format(new Date(iso));
  } catch {
    return iso;
  }
}

function centsToUsd(cents: number): string {
  if (cents === 0) return '$0';
  const dollars = cents / 100;
  if (dollars >= 1000) return `$${(dollars / 1000).toFixed(1)}k`;
  return `$${dollars.toFixed(0)}`;
}

function relativeDay(iso: string): string {
  const diff = Date.now() - new Date(iso).getTime();
  const days = Math.floor(diff / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Yesterday';
  return `${days}d ago`;
}

// ─── sub-components ──────────────────────────────────────────────────────

function SectionCard({
  icon,
  title,
  accent,
  children,
}: {
  icon: React.ReactNode;
  title: string;
  accent?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={cn('rounded-xl border bg-card p-5 shadow-sm', accent)}>
      <div className="mb-3 flex items-center gap-2">
        <span className="text-muted-foreground">{icon}</span>
        <h3 className="font-semibold text-sm uppercase tracking-wide text-muted-foreground">
          {title}
        </h3>
      </div>
      {children}
    </div>
  );
}

function OvernightSection({ bullets }: { bullets: string[] }) {
  return (
    <SectionCard icon={<Zap size={15} />} title="Last night OMEGA…">
      <ul className="space-y-2">
        {bullets.filter(Boolean).map((b, i) => (
          <li key={i} className="flex items-start gap-2 text-sm">
            <span className="mt-0.5 h-1.5 w-1.5 shrink-0 rounded-full bg-primary" />
            <span>{b}</span>
          </li>
        ))}
      </ul>
    </SectionCard>
  );
}

function DecisionsSection({ b }: { b: FounderBriefing }) {
  if (b.decisions_count === 0) {
    return (
      <SectionCard icon={<Brain size={15} />} title="Decisions awaiting you">
        <p className="text-sm text-muted-foreground">No pending decisions. All clear.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard
      icon={<Brain size={15} />}
      title={`Decisions awaiting you (${b.decisions_count})`}
      accent="border-amber-200 dark:border-amber-900"
    >
      <div className="space-y-3">
        {b.decisions_top3.map((d) => (
          <div key={d.decision_id} className="rounded-lg bg-amber-50 dark:bg-amber-950/30 p-3">
            <p className="text-sm font-medium">{d.question}</p>
            <p className="mt-1 text-xs text-muted-foreground">
              Confidence: <span className="capitalize">{d.confidence}</span>
              {' · '}Expires {new Date(d.expires_at).toLocaleDateString()}
            </p>
          </div>
        ))}
        {b.decisions_count > 3 && (
          <p className="text-xs text-muted-foreground">
            +{b.decisions_count - 3} more decision(s) awaiting review.
          </p>
        )}
      </div>
    </SectionCard>
  );
}

function ThreatsSection({ b }: { b: FounderBriefing }) {
  const hasCritical = b.threats_critical.length > 0;
  return (
    <SectionCard
      icon={<Shield size={15} />}
      title="Threats handled"
      {...(hasCritical ? { accent: 'border-red-300 dark:border-red-800' } : {})}
    >
      {b.threats_total === 0 ? (
        <p className="text-sm text-green-600 dark:text-green-400">
          No threats detected. Defense systems nominal.
        </p>
      ) : (
        <div className="space-y-2">
          <p className="text-sm">
            <span className="font-semibold">{b.threats_total}</span> threat(s) detected.{' '}
            {b.threats_all_handled ? (
              <span className="text-green-600 dark:text-green-400">All handled.</span>
            ) : (
              <span className="text-red-600 dark:text-red-400">
                {b.threats_critical.length} require attention.
              </span>
            )}
          </p>
          {b.threats_critical.map((t) => (
            <div key={t.id} className="rounded-lg bg-red-50 dark:bg-red-950/30 p-3">
              <p className="text-sm font-medium text-red-700 dark:text-red-300">
                <AlertTriangle size={12} className="inline me-1" />
                Critical: {t.description}
              </p>
            </div>
          ))}
          {b.threats_all_handled && (
            <Link
              to="/omega/defense"
              className="text-xs text-primary hover:underline inline-flex items-center gap-1"
            >
              View defense dashboard <ChevronRight size={12} />
            </Link>
          )}
        </div>
      )}
    </SectionCard>
  );
}

function RevenueSection({ b }: { b: FounderBriefing }) {
  const r = b.revenue;
  const net = r.net_usd_cents;
  return (
    <SectionCard icon={<DollarSign size={15} />} title="Revenue this window">
      <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        {[
          { label: 'Subscriptions', value: centsToUsd(r.subscriptions_usd_cents), color: 'text-green-600' },
          { label: 'Royalties', value: centsToUsd(r.royalties_usd_cents), color: 'text-blue-600' },
          { label: 'Churn', value: centsToUsd(r.churn_usd_cents), color: 'text-red-500' },
          {
            label: 'Net',
            value: centsToUsd(Math.abs(net)),
            color: net >= 0 ? 'text-green-700 dark:text-green-400' : 'text-red-600',
          },
        ].map(({ label, value, color }) => (
          <div key={label} className="rounded-lg bg-muted/40 p-2 text-center">
            <p className={cn('text-lg font-bold', color)}>{value}</p>
            <p className="text-xs text-muted-foreground">{label}</p>
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function MemorySection({ b }: { b: FounderBriefing }) {
  if (b.memory_insights.length === 0) {
    return (
      <SectionCard icon={<BookOpen size={15} />} title="Memory insights">
        <p className="text-sm text-muted-foreground">No memory insights surfaced this window.</p>
      </SectionCard>
    );
  }
  return (
    <SectionCard icon={<BookOpen size={15} />} title="Memory insights">
      <div className="space-y-3">
        {b.memory_insights.map((m) => (
          <div key={m.memory_id} className="border-s-2 border-primary/30 ps-3">
            <p className="text-sm">{m.summary}</p>
            {m.tags.length > 0 && (
              <div className="mt-1 flex flex-wrap gap-1">
                {m.tags.slice(0, 4).map((t) => (
                  <span
                    key={t}
                    className="rounded-full bg-primary/10 px-2 py-0.5 text-[10px] text-primary"
                  >
                    {t}
                  </span>
                ))}
              </div>
            )}
          </div>
        ))}
      </div>
    </SectionCard>
  );
}

function RecommendationSection({ b }: { b: FounderBriefing }) {
  const isCritical = b.threats_critical.length > 0;
  return (
    <SectionCard
      icon={<Sparkles size={15} />}
      title="Today's recommendation"
      accent={isCritical ? 'border-red-300 dark:border-red-800 bg-red-50 dark:bg-red-950/20' : 'bg-primary/5 border-primary/20'}
    >
      <p className="text-base font-medium">{b.recommendation}</p>
    </SectionCard>
  );
}

function ApprovalButton({ b }: { b: FounderBriefing }) {
  if (b.decisions_count === 0) return null;
  return (
    <div className="mt-6 flex justify-center">
      <Link
        to="/omega/console"
        className="inline-flex items-center gap-2 rounded-full bg-primary px-6 py-3 text-sm font-semibold text-primary-foreground shadow-md hover:bg-primary/90 transition-colors"
      >
        <Brain size={16} />
        Sign your daily approval ({b.decisions_count} decision{b.decisions_count !== 1 ? 's' : ''})
        <ChevronRight size={16} />
      </Link>
    </div>
  );
}

function BriefingArticle({ briefing }: { briefing: FounderBriefing }) {
  return (
    <article>
      {/* Header */}
      <div className="mb-6 border-b pb-4">
        <p className="text-xs uppercase tracking-widest text-muted-foreground">
          OMEGA Daily Briefing
        </p>
        <h1 className="mt-1 text-2xl font-bold sm:text-3xl">
          {formatDate(briefing.generated_at)}
        </h1>
        {briefing.sequence != null && (
          <p className="mt-1 text-xs text-muted-foreground">
            Log entry #{briefing.sequence} · {briefing.briefing_id}
          </p>
        )}
      </div>

      {/* Body sections */}
      <div className="space-y-4">
        <OvernightSection bullets={briefing.overnight_summary} />
        <DecisionsSection b={briefing} />
        <ThreatsSection b={briefing} />
        <RevenueSection b={briefing} />
        <MemorySection b={briefing} />
        <RecommendationSection b={briefing} />
      </div>

      <ApprovalButton b={briefing} />
    </article>
  );
}

function ArchiveList({
  selected,
  onSelect,
}: {
  selected: string;
  onSelect: (id: string) => void;
}) {
  const { data, isPending, isError } = useQuery({
    queryKey: ['briefing-history'],
    queryFn: () => api.getBriefingHistory(14),
    staleTime: 5 * 60_000,
    // Bound retries so a failing/absent endpoint settles to an empty state
    // instead of hanging on "Loading archive…" forever.
    retry: false,
  });

  if (isPending) {
    return <p className="text-sm text-muted-foreground">Loading archive…</p>;
  }

  // On error, fall through to the empty state ("No briefings yet.") rather than
  // never resolving.
  const items = isError ? [] : (data?.briefings ?? []);

  return (
    <div>
      <h2 className="mb-3 text-sm font-semibold uppercase tracking-wide text-muted-foreground">
        Archive
      </h2>
      <div className="space-y-1">
        {items.map((b) => (
          <button
            key={b.briefing_id}
            onClick={() => onSelect(b.briefing_id)}
            className={cn(
              'w-full rounded-lg px-3 py-2 text-start text-sm transition-colors',
              selected === b.briefing_id
                ? 'bg-primary/10 text-primary font-medium'
                : 'hover:bg-muted',
            )}
          >
            <span className="font-medium">{relativeDay(b.generated_at)}</span>
            <span className="ms-2 text-muted-foreground text-xs">
              {formatShortDate(b.generated_at)}
            </span>
            {b.decisions_count > 0 && (
              <span className="ms-2 rounded-full bg-amber-100 dark:bg-amber-900 px-1.5 py-0.5 text-[10px] text-amber-700 dark:text-amber-300">
                {b.decisions_count} pending
              </span>
            )}
            {b.threats_critical.length > 0 && (
              <span className="ms-1 rounded-full bg-red-100 dark:bg-red-900 px-1.5 py-0.5 text-[10px] text-red-700 dark:text-red-300">
                critical
              </span>
            )}
          </button>
        ))}
        {items.length === 0 && (
          <p className="text-xs text-muted-foreground px-3 py-2">No briefings yet.</p>
        )}
      </div>
    </div>
  );
}

// ─── main page ───────────────────────────────────────────────────────────────

export function OmegaBriefingPage() {
  const [selectedId, setSelectedId] = useState<string | null>(null);

  const {
    data: today,
    isLoading,
    error,
  } = useQuery({
    queryKey: ['briefing-today'],
    queryFn: () => api.getBriefingToday(),
    staleTime: 5 * 60_000,
    retry: false,
  });

  const { data: history } = useQuery({
    queryKey: ['briefing-history'],
    queryFn: () => api.getBriefingHistory(14),
    staleTime: 5 * 60_000,
    retry: false,
  });

  if (!isOmegaAdmin()) {
    return (
      <div className="flex items-center justify-center py-16 text-muted-foreground text-sm">
        Admin access required.
      </div>
    );
  }

  // Determine which briefing to display
  const displayBriefing =
    selectedId != null
      ? (history?.briefings.find((b) => b.briefing_id === selectedId) ?? today)
      : today;

  return (
    <div className="mx-auto max-w-6xl px-4 py-6">
      {/* Page title bar */}
      <div className="mb-6 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <TrendingUp size={20} className="text-primary" />
          <h1 className="text-xl font-bold">Founder Briefing</h1>
        </div>
        <div className="flex items-center gap-2 text-xs text-muted-foreground">
          <Clock size={13} />
          Daily at 07:00 Israel time
        </div>
      </div>

      {/* Two-column layout: article + archive sidebar */}
      <div className="flex flex-col gap-6 lg:flex-row">
        {/* Main article column */}
        <main className="min-w-0 flex-1">
          {isLoading && (
            <div className="space-y-4">
              {[1, 2, 3].map((i) => (
                <div key={i} className="h-24 rounded-xl bg-muted animate-pulse" />
              ))}
            </div>
          )}

          {!isLoading && error && (
            <div className="rounded-xl border border-amber-200 dark:border-amber-800 bg-amber-50 dark:bg-amber-950/30 p-6 text-center">
              <p className="font-medium text-amber-800 dark:text-amber-200">
                No briefing generated yet
              </p>
              <p className="mt-1 text-sm text-muted-foreground">
                Briefings are generated automatically every day at 07:00 Israel time, or you
                can trigger one manually via the admin API.
              </p>
            </div>
          )}

          {!isLoading && !error && displayBriefing && (
            <BriefingArticle briefing={displayBriefing} />
          )}
        </main>

        {/* Sidebar: archive */}
        <aside className="w-full lg:w-56 xl:w-64 shrink-0">
          <ArchiveList
            selected={selectedId ?? (today?.briefing_id ?? '')}
            onSelect={setSelectedId}
          />
        </aside>
      </div>
    </div>
  );
}
